import _ from 'lodash';
import { useMemo } from 'react';

import { DwhSriCategory } from '@ndvr/market-data-api';

import { tickerLogoUrl } from '@/components/ticker-logo-url';
import { useRestrictionsData } from 'views/portfolio/PortfolioPage/tabs/equity-preferences/useRestrictionsData';
import { useStockData } from 'views/portfolio/PortfolioPage/utils/useStockData';

import { type AffectedStock } from './AffectedStocksTable';
import { type RestrictedStockWithCategories } from './AllRestrictedStocksSection';

export interface CategoryStats {
    totalCategories: number;
    excludedStocks: number;
    tradableStocks: number;
    excludedMarketValue: number;
    excludedMarketShare: number;
}

export interface CategoryStockData {
    allExcludedSubCategoryStocks: string[];
    excludedStocks: string[];
    tradableStocks: string[];
}

export const useSRIRestrictions = (accountGroupGUID: string) => {
    const { stocks, isLoading: isStockDataLoading, isFetching: isStockDataFetching } = useStockData();

    const stocksMap = useMemo(
        () =>
            new Map(
                stocks.map((stock) => [
                    stock.shareId,
                    { sId: stock.shareId, symbol: stock.tickerSymbol, description: stock.companyName },
                ]),
            ),
        [stocks],
    );

    const stocksByShareClassId = useMemo(() => new Map(stocks.map((stock) => [stock.shareClassId, stock])), [stocks]);

    const { restrictionsInfo, preferences } = useRestrictionsData(accountGroupGUID, stocksMap);

    const mapShareClassIdToAffectedStock = (shareClassId: string, excludedStocks: string[]): AffectedStock | null => {
        const stock = stocksByShareClassId.get(shareClassId);
        if (!stock) return null;

        const isRestricted = excludedStocks.includes(shareClassId);

        return {
            shareClassId: shareClassId,
            shareId: stock.shareId,
            ticker: stock.tickerSymbol || 'N/A',
            name: stock.companyName || 'N/A',
            marketValue: stock.marketCap ? parseFloat(stock.marketCap) : 0,
            percentage: stock.marketCapWeight ? parseFloat(stock.marketCapWeight) : 0,
            restricted: isRestricted,
            logoUrl: tickerLogoUrl(stock.tickerSymbol),
        } as AffectedStock;
    };

    const isSubCategoryExcluded = (categoryId: string, subCategoryId: string) => {
        return Boolean(
            restrictionsInfo?.sriRestrictions?.[categoryId]?.some(
                (restriction) => restriction.subCategoryId === subCategoryId,
            ),
        );
    };

    const calculateMarketValueForStocks = (shareClassIds: string[]): { marketValue: number; marketShare: number } => {
        let sumMarketCap = 0;
        let sumMarketShare = 0;

        shareClassIds.forEach((shareClassId) => {
            const stock = stocksByShareClassId.get(shareClassId);
            sumMarketCap += parseFloat(stock?.marketCap || '0');
            sumMarketShare += parseFloat(stock?.marketCapWeight || '0');
        });

        return { marketValue: sumMarketCap, marketShare: sumMarketShare };
    };

    const getCategoryStockData = (category: DwhSriCategory): CategoryStockData => {
        const allExcludedSubCategoryStocks = _(category.subCategories)
            .filter((subCategory) => isSubCategoryExcluded(category.sriCategoryId, subCategory.sriSubCategoryId))
            .map((subCategory) => subCategory.scIds)
            .flatten()
            .uniq()
            .difference(_.compact((preferences?.restrictions || []).map((r) => r.shareClassId)))
            .value();

        const excludedStocks = _(allExcludedSubCategoryStocks)
            .difference(_.compact(restrictionsInfo?.overrides?.map((o) => o.shareClassId) || []))
            .value();

        const tradableStocks = _(allExcludedSubCategoryStocks).difference(excludedStocks).value();

        return {
            allExcludedSubCategoryStocks,
            excludedStocks,
            tradableStocks,
        };
    };

    const getAffectedStocksForSelection = (
        category: DwhSriCategory,
        selectedSubCategories: string[],
    ): AffectedStock[] => {
        // Create a temporary category object with only selected sub-categories to determine affected stocks
        const tempCategory = {
            ...category,
            subCategories: category.subCategories.filter((subCategory) =>
                selectedSubCategories.includes(subCategory.sriSubCategoryId),
            ),
        };

        const allExcludedSubCategoryStocks = _(tempCategory.subCategories)
            .map((subCategory) => subCategory.scIds)
            .flatten()
            .uniq()
            .value();

        const excludedStocks = _(allExcludedSubCategoryStocks)
            .difference(_.compact(restrictionsInfo?.overrides?.map((o) => o.shareClassId) || []))
            .value();

        // Get actual stock data for the affected stocks
        const affectedStockData = allExcludedSubCategoryStocks
            .map((shareClassId: string) => mapShareClassIdToAffectedStock(shareClassId, excludedStocks))
            .filter(Boolean)
            .sort((a, b) => a?.ticker?.localeCompare(b?.ticker || '') || 0) as AffectedStock[];

        return affectedStockData;
    };

    const getAllStocksForCategory = (category: DwhSriCategory): AffectedStock[] => {
        // Create a temporary category object with only selected sub-categories to determine affected stocks

        const allSubCategoryStocks = _(category.subCategories)
            .map((subCategory) => subCategory.scIds)
            .flatten()
            .uniq()
            .value();

        const excludedStocks = _(allSubCategoryStocks)
            .difference(_.compact(restrictionsInfo?.overrides?.map((o) => o.shareClassId) || []))
            .value();

        const allStockData = allSubCategoryStocks
            .map((shareClassId: string) => mapShareClassIdToAffectedStock(shareClassId, excludedStocks))
            .filter(Boolean)
            .sort((a, b) => a?.ticker?.localeCompare(b?.ticker || '') || 0) as AffectedStock[];

        return allStockData;
    };

    const getAffectedStocks = (category: any): AffectedStock[] => {
        const { allExcludedSubCategoryStocks, excludedStocks } = getCategoryStockData(category);

        // Get actual stock data for the affected stocks
        const affectedStockData = allExcludedSubCategoryStocks
            .map((shareClassId: string) => mapShareClassIdToAffectedStock(shareClassId, excludedStocks))
            .filter(Boolean)
            .sort((a, b) => a?.ticker?.localeCompare(b?.ticker || '') || 0) as AffectedStock[];

        return affectedStockData;
    };

    const getCategoryStats = (category: any): CategoryStats => {
        const { excludedStocks, tradableStocks } = getCategoryStockData(category);

        const { marketValue: excludedMarketValue, marketShare: excludedMarketShare } =
            calculateMarketValueForStocks(excludedStocks);

        return {
            totalCategories: category?.subCategories?.length || 0,
            excludedStocks: excludedStocks.length,
            tradableStocks: tradableStocks.length,
            excludedMarketValue,
            excludedMarketShare,
        };
    };

    const getAllRestrictedStocks = (): RestrictedStockWithCategories[] => {
        const stockCategoryMap = new Map<string, RestrictedStockWithCategories>();

        restrictionsInfo?.categories?.forEach((category) => {
            const { excludedStocks } = getCategoryStockData(category);

            excludedStocks.forEach((shareClassId) => {
                const stock = stocksByShareClassId.get(shareClassId);
                if (!stock) return;

                const categoryInfo = {
                    categoryId: category.sriCategoryId,
                    categoryLabel: category.sriCategoryLabel || 'Unknown Category',
                };

                if (stockCategoryMap.has(shareClassId)) {
                    const existingStock = stockCategoryMap.get(shareClassId)!;
                    existingStock.restrictedCategories.push(categoryInfo);
                } else {
                    stockCategoryMap.set(shareClassId, {
                        shareClassId: shareClassId,
                        shareId: stock.shareId,
                        ticker: stock.tickerSymbol || 'N/A',
                        name: stock.companyName || 'N/A',
                        marketValue: stock.marketCap ? parseFloat(stock.marketCap) : 0,
                        percentage: stock.marketCapWeight ? parseFloat(stock.marketCapWeight) : 0,
                        logoUrl: tickerLogoUrl(stock.tickerSymbol),
                        restrictedCategories: [categoryInfo],
                    });
                }
            });
        });

        return Array.from(stockCategoryMap.values());
    };

    return {
        stocks,
        restrictionsInfo,
        preferences,
        isSubCategoryExcluded,
        getCategoryStats,
        getAllStocksForCategory,
        getAffectedStocks,
        getAffectedStocksForSelection,
        getAllRestrictedStocks,
        isStockDataLoading: isStockDataLoading || isStockDataFetching,
    };
};
